import { useEffect, useState } from 'react';

import { debounce } from '../Utils/Debounce';

type ScrollState = Record<string, boolean>;
type Thresholds = Record<string, number>;

/**
 * Hook to handle multiple different states at different scroll positions
 * The thresholds you feed in will provide the keys of the state on the way out
 *
 * Input:
 * ```
 * const thresholds = {
 *     activateFireworks: 10,
 *     dancingHorses: 300,
 * }
 *
 * Output:
 * const state = {
 *     activateFireworks: true,
 *     dancingHorses: false,
 * }
 */
export const useScrollState = ({
  thresholds,
  debounceTimeout,
  isDisabled,
}: {
  thresholds: Thresholds;
  debounceTimeout?: number;
  isDisabled: boolean;
}) => {
  const createInitialState = () =>
    Object.keys(thresholds).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {} as ScrollState);

  const [scrollState, setScrollState] = useState<ScrollState>(createInitialState());

  const setStateOnScroll = () => {
    setScrollState(prevState => {
      let hasChanged = false;
      const updatedState = { ...prevState };

      for (const key in thresholds) {
        const isPastThreshold = window.scrollY > thresholds[key];

        if (prevState[key] !== isPastThreshold) {
          updatedState[key] = isPastThreshold;
          hasChanged = true;
        }
      }

      return hasChanged ? updatedState : prevState;
    });
  };

  const handleScroll = debounceTimeout
    ? debounce(setStateOnScroll, debounceTimeout)
    : setStateOnScroll;

  useEffect(() => {
    if (isDisabled) {
      setScrollState(createInitialState());
      globalThis.removeEventListener('scroll', handleScroll);
    } else {
      globalThis.addEventListener('scroll', handleScroll);
    }

    return () => {
      globalThis.removeEventListener('scroll', handleScroll);
    };
  }, [JSON.stringify(thresholds), isDisabled]);

  return {
    scrollState,
  };
};
